import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Flex, Result, Skeleton, Typography } from '@/shared/antd-imports';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useGetClientUpdateQuery } from '@/store/updates-api';
import { formatCalendarDate } from '@/utils/money';
import { renderUpdateBody } from '@/lib/updates/markdown';
import { DisclosureTag } from '@/components/updates/AgentProgressChip';
import { UpdateEvidenceGallery } from '@/components/updates/UpdateEvidenceGallery';

/**
 * Spec 28 §7.5 (stream abill): one published update — the text (minimal Markdown rendered as elements, never HTML),
 * the shared screenshots and recordings, and the AI-assisted label when the provider turned it on.
 */
const UpdateDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useGetClientUpdateQuery(id ?? '', { skip: !id });
  const update = data?.done ? data.body : null;
  const isNotFound = (error as { status?: number } | undefined)?.status === 404;

  const back = (
    <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/updates')} style={{ paddingInline: 0 }}>
      {t('updates.back', { defaultValue: 'All updates' })}
    </Button>
  );

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (isNotFound) {
    return (
      <Result
        status="404"
        title={t('updates.notFound', { defaultValue: 'This update is not available.' })}
        subTitle={t('updates.notFoundHint', { defaultValue: 'It may have been withdrawn by your project team.' })}
        extra={back}
      />
    );
  }
  if (isError || !update) {
    return (
      <Flex vertical gap={12}>
        {back}
        <Alert
          type="error"
          showIcon
          message={t('updates.detailError', { defaultValue: 'The update could not be loaded.' })}
          action={<Button size="small" onClick={() => void refetch()}>{t('updates.retry', { defaultValue: 'Retry' })}</Button>}
        />
      </Flex>
    );
  }
  const period =
    update.period_from === update.period_to
      ? formatCalendarDate(update.period_from, i18n.language)
      : `${formatCalendarDate(update.period_from, i18n.language)} – ${formatCalendarDate(update.period_to, i18n.language)}`;

  return (
    <Flex vertical gap={12} style={{ maxWidth: 880, width: '100%' }} data-testid="update-details">
      {back}
      <Card>
        <Flex vertical gap={8}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {update.project_name} · {period}
          </Typography.Text>
          <Typography.Title level={3} style={{ margin: 0, wordBreak: 'break-word' }}>
            {update.title}
          </Typography.Title>
          <Flex gap={8} wrap align="center">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('updates.publishedOn', { defaultValue: 'Published {{date}}', date: formatCalendarDate(update.published_at, i18n.language) ?? '' })}
            </Typography.Text>
            {update.ai_assisted && <DisclosureTag disclosure={update.disclosure} label={update.disclosure_label} />}
          </Flex>
          <div className="client-update-body" style={{ fontSize: 14, lineHeight: 1.6, wordBreak: 'break-word' }}>
            {renderUpdateBody(update.body)}
          </div>
          <UpdateEvidenceGallery updateId={update.id} evidence={update.evidence} />
        </Flex>
      </Card>
    </Flex>
  );
};

export default UpdateDetailsPage;
